import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const TITLE = "Why curated storefronts outperform link-in-bio";
const DESCRIPTION =
  "Link-in-bio tools were built for traffic, not taste. Here's why editorial storefronts convert two to three times better.";
const PATH = "/journal/why-curated-storefronts-outperform-link-in-bio";

export const Route = createFileRoute("/journal_/why-curated-storefronts-outperform-link-in-bio")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-10",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: TITLE, item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav aria-label="Breadcrumb" className="relative mx-auto max-w-3xl px-6 pt-10">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li><Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Storefronts vs link-in-bio</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Essay · 5 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            Why curated storefronts <span className="italic shimmer">outperform</span> link-in-bio
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 text-sm text-muted-foreground">By the Pluggz Editorial Team · June 2026</p>
        </Reveal>
      </header>

      <Reveal>
        <div className="relative mx-auto mb-12 max-w-5xl px-6">
          <div className="aspect-[16/7] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-accent/30 via-card to-background ring-1 ring-border/60" />
        </div>
      </Reveal>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing space-y-6 leading-relaxed">
          <p className="text-lg">
            Link-in-bio tools solved an Instagram problem in 2017: the bio held a single URL, and creators needed somewhere to send people. The tools that emerged — link lists, button stacks, beacon pages — were essentially traffic routers. They were never designed to sell. Nearly a decade later, most creators are still using a button stack to do the work of a magazine, and the conversion data shows it.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The button-stack problem</h2>
          <p className="text-muted-foreground">
            A link-in-bio page presents every option as equal. Newsletter, podcast, latest video, brand collab, three affiliate links, a sale, a Discord invite. It's a menu without a chef's recommendation. Shoppers arriving with intent get distracted; shoppers arriving without intent get nothing to anchor on. Across the storefronts we benchmarked in early 2026, button-stack pages converted at 0.6% to 1.1% — roughly the rate of an undifferentiated banner ad.
          </p>

          <h2 className="mt-12 font-serif text-3xl">What an editorial storefront does differently</h2>
          <p className="text-muted-foreground">
            An editorial storefront opens with a single, clearly-stated point of view, groups products into a small number of considered collections, writes a real sentence next to each item, and ends with a frictionless path to the brand's checkout. It treats the shopper as someone who came to browse a curated edit, not to pick a destination from a hub. The same audience, sent to a storefront instead of a link list, converts at 2.4% to 3.8% — roughly three times the rate.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Why curation beats coverage</h2>
          <p className="text-muted-foreground">
            The instinct, when a tool gives you unlimited slots, is to fill them. Resist it. The strongest storefronts on Pluggz have fewer than forty products, are updated monthly rather than weekly, and read like a magazine column. Shoppers don't reward completeness; they reward taste. A creator who has the discipline to leave things off the page tells the audience that what made it on was chosen.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The two-tap rule, again</h2>
          <p className="text-muted-foreground">
            The other quiet edge of curated storefronts is what happens after the click. Link-in-bio tools tend to route shoppers through an aggregator before reaching the brand site, sometimes adding two or three hops. Each hop costs roughly 15% of intent. A curated storefront with two-tap checkout — product to brand cart — recovers that loss, and the recovery alone often doubles a creator's effective commission.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The case for migrating</h2>
          <p className="text-muted-foreground">
            None of this means deleting your link-in-bio overnight. The link list still earns its keep as a hub for non-commercial destinations — your newsletter, your podcast, your latest video. But the shopping should live somewhere it was designed to live: in a storefront built for it. The creators who have moved their commerce out of the button stack and into a curated edit are, almost without exception, earning more on the same audience. The audience hasn't changed. The container has.
          </p>

          <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
            <p className="editorial-eyebrow">Keep reading</p>
            <h3 className="mt-3 font-serif text-2xl">More from the Journal</h3>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              <Link to="/journal" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                Back to the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}